import { ObjectHelper } from "@bryntum/schedulerpro";

function persistableFieldsData(record) {
  return record.fields.reduce((data, field) => {
    const value = record.getFieldPersistentValue(field);

    if (value !== undefined) {
      data[field.name] = value;
    }

    return data;
  }, {});
}

// When we add record we need to use field names, not data source. But when we loadData to store
// we need to use data source still
function add(records) {
  records.length && this.add(records.map(persistableFieldsData));
}

function remove(records) {
  this.remove(records.map((r) => r.id));
}

function update(record, changes) {
  let targetRecord;

  // In case `id` field is in changes we need to resolve record by the old id
  if ("id" in changes) {
    targetRecord = this.getById(changes.id.oldValue);
    if (targetRecord) {
      targetRecord.id = changes.id.value;
      delete changes.id;
    } else {
      targetRecord = this.getById(changes.id.value);
    }
  } else {
    targetRecord = this.getById(record.id);
  }

  if (targetRecord) {
    const changesToApply = {};

    for (const key in changes) {
      changesToApply[key] = changes[key].value;
    }

    targetRecord.set(changesToApply);
  } else {
    // If we don't find record in the store put a warning to the console. Case should
    // be investigated
    // console.warn("Could not find record in the connected store to update");
  }
}

function filterChanges(primaryStore, secondaryStore, changes) {
  const fieldMap =
    primaryStore._swivu_connected_stores_fieldmap[secondaryStore.storeId];
  const result = {};

  for (const field in fieldMap) {
    if (field in changes) {
      const newKey = fieldMap[field];
      const value = changes[field];
      delete changes[field];

      // If new key is provided, put change back under new id
      if (newKey) {
        if (typeof newKey === "function") {
          Object.assign(result, newKey(value));
        } else {
          result[newKey] = value;
        }
      }
    }
  }

  return Object.assign({}, changes, result);
}

const listeners = {
  // change method scope is alway store we need to apply changes to
  change({ source, action, record, records, changes }) {
    if (source._sw_ignore_events) {
      return;
    }

    this._sw_ignore_events = true;

    switch (action) {
      case "add": {
        // Do not add records that are still being created
        const toAdd = records.filter((r) => !r.isCreating);

        add.call(this, toAdd);
        break;
      }
      case "remove":
        remove.call(this, records);
        break;
      case "update":
        update.call(this, record, filterChanges(source, this, changes));
        break;
      default:
        break;
    }

    this._sw_ignore_events = false;
  },
  addConfirmed({ source, record }) {
    if (source._sw_ignore_events) {
      return;
    }

    this._sw_ignore_events = true;

    add.call(this, [record]);

    this._sw_ignore_events = false;
  }
};

export const SwivuConnectedStoreMixin = (Target) =>
  class SwivuConnectedStoreMixin extends Target {
    static get $name() {
      return "SwivuConnectedStoreMixin";
    }

    construct(config) {
      super.construct(config);

      this._swivu_connected_stores = [];
      this._swivu_connected_stores_fieldmap = {};
    }

    get data() {
      return super.data;
    }

    set data(data) {
      // If incoming value is not an array or array of model instances, clean up data in connected stores
      if (!Array.isArray(data) || !data.length || data[0].isModel) {
        super.data = data;

        // needed to add optional chaining opt since app is breaking when chaining extraTaskStore
        this._swivu_connected_stores?.forEach((store) => (store.data = []));
      } else {
        // populating store data will use original object is `record.originalData` meaning it would be
        // shared between records unless cloned.
        super.data = ObjectHelper.clone(data);

        this._swivu_connected_stores.forEach(
          (store) => (store.data = ObjectHelper.clone(data))
        );
      }
    }

    connectSwivuStore(store, fieldMap = {}) {
      if (this._swivu_connected_stores.includes(store)) {
        return;
      }

      this._swivu_connected_stores.push(store);

      // build unique listener id
      const primaryStoreListenersName = `${this.storeId}-${store.storeId}`;
      const connectedStoreListnersName = `${store.storeId}-${this.storeId}`;

      this._swivu_connected_stores_fieldmap[store.storeId] = fieldMap;

      // detach previous listeners
      this.detachListeners(primaryStoreListenersName);
      store.detachListeners(connectedStoreListnersName);

      // this way we're listening to changes on the primaryStore and listener scope is store
      this.on(
        Object.assign({}, listeners, {
          name: primaryStoreListenersName,
          thisObj: store
        })
      );

      store.on({
        name: connectedStoreListnersName,
        beforeRemove({ records }) {
          // Check if record we're trying to remove exists in the master store. if it does - we need to return false
          // to cancel removal and try remove records from the master store. That would trigger correct confirmation.
          // If records do not exist in the master store - we can just silently remove them following the lead
          if (this._swivuPrimaryStore.includes(records[0].id)) {
            // Remove will be cancelled too while awaiting for user's confirmation. In case remove will actually remove records
            // synchronously, we'll get into this listener once again before reaching the return statement. But nevertheless it
            // should not be a problem
            this._swivuPrimaryStore.remove(records.map((r) => r.id));
            return false;
          } else {
            return true;
          }
        }
      });

      store._swivuPrimaryStore = this;
    }

    disconnectSwivuStore(store) {
      // I don't see why we would need to disconnect any store at this point. But just in case, here
      // goes the cleanup
      this._swivu_connected_stores.splice(
        this._swivu_connected_stores.indexOf(store),
        1
      );
      delete this._swivu_connected_stores_fieldmap[store.storeId];

      this.detachListeners(`${this.storeId}-${store.storeId}`);
      store.detachListeners(`${store.storeId}-${this.storeId}`);
    }

    // This is a public method which is called when applying changes from the `sync` response. It is
    // required when server response contains project fields (start data, duration, etc). In this case
    // events like change/idChange get suspended
    applyChangeset(changes, transformFn, phantomIdField, ...rest) {
      // Raise a flag to make sure change event listener does not do anything
      this._sw_ignore_events = true;

      const result = super.applyChangeset(
        changes,
        transformFn,
        phantomIdField,
        ...rest
      );

      this._swivu_connected_stores.forEach((store) => {
        store.applyChangeset(changes, transformFn, phantomIdField);
      });

      this._sw_ignore_events = false;

      return result;
    }
  };
